import { canAccess } from "@/middlewares/auth";

export const STATUS_LABELS = {
  pending_assignment: "Awaiting Assignment",
  pending_approval: "Awaiting Approval",
  needs_revision: "Needs Revision",
  approved: "Approved",
  ongoing: "Ongoing",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending_assignment", label: "Awaiting Assignment" },
  { value: "pending_approval", label: "Awaiting Approval" },
  { value: "needs_revision", label: "Needs Revision" },
  { value: "approved", label: "Approved" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export function tripStatusLabel(status) {
  return STATUS_LABELS[status] || status;
}

export function tripStatusClass(status) {
  return `tt-status tt-status-${status || "cancelled"}`;
}

/** Date-only fields (trip_date). Accepts Y-m-d or ISO datetimes. */
export function fmtTripDate(v, options = { dateStyle: "medium" }) {
  if (!v) return "—";
  const raw = String(v);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw;
  const d = new Date(dateOnly);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-PH", options);
}

/** Long date for official print forms. */
export function fmtTripDateLong(v) {
  if (!v) return "";
  const raw = String(v);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw;
  const d = new Date(dateOnly);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "2-digit" });
}

/** Date+time fields. */
export function fmtTripDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });
}

export function fmtTripTime(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit", hour12: true });
}

/** Print is allowed once the trip is approved (or later). */
export function canPrintTrip(trip) {
  if (!trip || !trip.vehicle) return false;
  return ["approved", "ongoing", "completed"].includes(trip.status);
}

/** Gatepass for the guard — approved / ongoing trips with a vehicle. */
export function canPrintGatepass(trip) {
  if (!trip || !trip.vehicle) return false;
  return ["approved", "ongoing"].includes(trip.status);
}

export function placesLabel(t) {
  if (!t) return "—";
  const visited = t.places_visited && t.places_visited !== "—" ? t.places_visited : "";
  return visited
    || t.destination_label
    || (t.destination && t.destination.destination_name)
    || "—";
}

export function requesterName(t) {
  if (!t || !t.creator) return "—";
  return `${t.creator.first_name || ""} ${t.creator.last_name || ""}`.trim() || t.creator.email || "—";
}

export function userDisplayName(u) {
  if (!u) return "";
  return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "";
}

export function passengerNames(trip) {
  if (!trip) return "";
  return (trip.passengers || [])
    .map((tp) => (tp.passenger ? userDisplayName(tp.passenger) : ""))
    .filter(Boolean)
    .join(", ");
}

export function passengerCount(trip) {
  if (!trip) return 0;
  if (trip.passenger_count != null) return Number(trip.passenger_count);
  if (trip.passengers_count != null) return Number(trip.passengers_count);
  return (trip.passengers || []).length;
}

export function stopCount(trip) {
  if (!trip || !Array.isArray(trip.stops)) return 0;
  return trip.stops.filter((s) => s && (s.label || s.lat || s.lng)).length;
}

/** Compact “Updated …” label for list tables. */
export function fmtUpdatedAt(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Elapsed time from office departure to return (or to now if still ongoing).
 * Uses actual_departure_datetime → actual_arrival_datetime.
 */
export function fmtTripDuration(trip) {
  if (!trip?.actual_departure_datetime) return "—";
  const start = new Date(trip.actual_departure_datetime);
  if (Number.isNaN(start.getTime())) return "—";

  const endRaw = trip.actual_arrival_datetime;
  const end = endRaw ? new Date(endRaw) : trip.status === "ongoing" ? new Date() : null;
  if (!end || Number.isNaN(end.getTime()) || end < start) return "—";

  const totalMins = Math.round((end.getTime() - start.getTime()) / 60000);
  if (totalMins < 1) return "<1m";
  const days = Math.floor(totalMins / (60 * 24));
  const hours = Math.floor((totalMins % (60 * 24)) / 60);
  const mins = totalMins % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins || !parts.length) parts.push(`${mins}m`);
  return parts.join(" ");
}

export function tripApiError(e) {
  const d = e?.response?.data;
  if (d?.errors) return Object.values(d.errors).flat()[0];
  return d?.message || "Request failed.";
}

export function todayYmd() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function canCancelTrip(t, user) {
  if (!t || !user) return false;

  const pending = ["pending_assignment", "pending_approval", "needs_revision"];
  if (pending.includes(t.status)) {
    if (user.role === "Administrator") return true;
    if (Number(t.created_by) === Number(user.id)) return true;
    return canAssignTrips(user);
  }

  // Approved: dispatcher / GSO, assigned driver, or admin.
  if (t.status === "approved") {
    if (user.role === "Administrator") return true;
    if (canAssignTrips(user)) return true;
    const driverUserId = t.driver?.user_id ?? t.driver?.user?.id;
    return driverUserId != null && Number(driverUserId) === Number(user.id);
  }

  return false;
}

/** Owner can edit when sent back; Administrators can edit any pending request. */
export function canEditTrip(t, user) {
  if (!t || !user) return false;
  if (user.role === "Administrator") {
    return ["pending_assignment", "pending_approval", "needs_revision"].includes(t.status);
  }
  return t.status === "needs_revision" && Number(t.created_by) === Number(user.id);
}

/** Role/permission helpers for contextual actions on trip detail. */
export function canAssignTrips(user) {
  const role = user?.role;
  return role === "Administrator" || role === "general_services" || canAccess("trip_assign", role);
}

export function canApproveTrips(user) {
  const role = user?.role;
  return role === "Administrator" || role === "chief_admin" || canAccess("trip_approve", role);
}

export function canDriveTrips(user) {
  const role = user?.role;
  return role === "Administrator" || role === "driver" || canAccess("trip_driver", role);
}

export function isTripOwner(trip, user) {
  if (!trip || !user) return false;
  return Number(trip.created_by) === Number(user.id) || user.role === "Administrator";
}

export function formatKm(v) {
  return v !== null && v !== undefined && v !== "" ? Number(v).toFixed(2) : "—";
}

export function formatOdo(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-PH", { maximumFractionDigits: 2 });
}
